import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Grid,
  Divider,
  Container,
  SelectChangeEvent,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  DirectionsCar as CarIcon,
  PlayArrow as StartIcon,
  Stop as CompleteIcon,
  Cancel as CancelIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { format, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import AuthLayout from '../components/AuthLayout';
import routeService, { Route, RouteParams } from '../services/route.service';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

const RouteList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Estado para armazenar as rotas
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados para paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRoutes, setTotalRoutes] = useState(0);
  
  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Estados para diálogo de exclusão
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState<Route | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Estado para snackbar
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Dados de exemplo para quando a API não estiver disponível
  const mockRoutes: Route[] = [
    {
      _id: '1',
      name: 'Rota Norte - Manutenção Preventiva',
      description: 'Manutenção preventiva em equipamentos da região norte',
      technician: { name: 'João Silva', _id: 't1' },
      executionDate: new Date().toISOString(),
      status: 'pending',
      totalDistance: 15000,
      estimatedTime: 3600,
      startLocation: { lat: -23.5505, lng: -46.6333 },
      waypoints: [],
      createdAt: new Date().toISOString()
    },
    {
      _id: '2',
      name: 'Rota Sul - Instalações',
      description: 'Instalação de novos equipamentos na região sul',
      technician: { name: 'Maria Oliveira', _id: 't2' },
      executionDate: new Date(Date.now() - 86400000).toISOString(), // Ontem
      status: 'in_progress',
      totalDistance: 8500,
      estimatedTime: 1800,
      startLocation: { lat: -23.6505, lng: -46.7333 },
      waypoints: [],
      startTime: new Date(Date.now() - 3600000).toISOString(),
      createdAt: new Date(Date.now() - 86400000).toISOString()
    },
    {
      _id: '3',
      name: 'Rota Leste - Atendimentos Urgentes',
      description: 'Atendimentos urgentes na região leste',
      technician: { name: 'Carlos Santos', _id: 't3' },
      executionDate: new Date(Date.now() - 172800000).toISOString(), // 2 dias atrás
      status: 'completed',
      totalDistance: 12000,
      estimatedTime: 2400,
      startLocation: { lat: -23.5505, lng: -46.5333 },
      waypoints: [],
      startTime: new Date(Date.now() - 172800000).toISOString(),
      endTime: new Date(Date.now() - 169200000).toISOString(),
      createdAt: new Date(Date.now() - 172800000).toISOString()
    }
  ];

  // Função para buscar as rotas
  const fetchRoutes = async (params: RouteParams = {}) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Iniciando busca de rotas...');
      
      const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
      const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;
      
      try {
        console.log('Parâmetros de busca:', {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
          status: statusFilter || undefined,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          sortBy: 'executionDate',
          sortOrder: 'desc',
          ...params
        });
        
        const response = await routeService.getRoutes({
          page: page + 1, // API usa indexação baseada em 1
          limit: rowsPerPage,
          search: searchTerm,
          status: statusFilter || undefined,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          sortBy: 'executionDate',
          sortOrder: 'desc',
          ...params
        });
        
        console.log('Resposta completa da API de rotas:', response);
        
        if (response.error) {
          console.error('Erro retornado pela API:', response.error);
          throw new Error(response.error);
        } else {
          console.log('Rotas recebidas:', response.routes);
          console.log('Informações de paginação:', response.pagination);
          
          setRoutes(response.routes || []);
          setTotalRoutes(response.pagination?.total || 0);
          
          console.log('Estado atualizado - rotas:', response.routes?.length || 0);
          console.log('Estado atualizado - total:', response.pagination?.total || 0);
        }
      } catch (apiError) {
        console.warn('API não disponível, usando dados de exemplo:', apiError);
        
        // Filtrar os dados de exemplo com base nos filtros
        let filteredRoutes = [...mockRoutes];
        
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          filteredRoutes = filteredRoutes.filter(route => 
            route.name.toLowerCase().includes(searchLower) || 
            (route.description?.toLowerCase().includes(searchLower))
          );
        }
        
        if (statusFilter) {
          filteredRoutes = filteredRoutes.filter(route => route.status === statusFilter);
        }
        
        if (startDate) {
          const startDateObj = startOfDay(startDate);
          filteredRoutes = filteredRoutes.filter(route => {
            if (!route.executionDate) return false;
            return new Date(route.executionDate) >= startDateObj;
          });
        }
        
        if (endDate) {
          const endDateObj = endOfDay(endDate);
          filteredRoutes = filteredRoutes.filter(route => {
            if (!route.executionDate) return false;
            return new Date(route.executionDate) <= endDateObj;
          });
        }
        
        // Aplicar paginação aos dados filtrados
        const start = page * rowsPerPage;
        const paginatedRoutes = filteredRoutes.slice(start, start + rowsPerPage);
        
        setRoutes(paginatedRoutes);
        setTotalRoutes(filteredRoutes.length);
        setError('API não disponível. Exibindo dados de exemplo.');
      }
    } catch (err: any) {
      console.error('Erro ao buscar rotas:', err);
      setError('Falha ao carregar rotas. Por favor, tente novamente.');
      setRoutes([]);
      setTotalRoutes(0);
    } finally {
      setLoading(false);
    }
  };

  // Carregar rotas quando os filtros ou paginação mudarem
  useEffect(() => {
    fetchRoutes();
  }, [page, rowsPerPage, searchTerm, statusFilter, startDate, endDate]);

  // Manipuladores de eventos para paginação
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Manipuladores de eventos para filtros
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  // Manipuladores para ações de rota
  const handleAddRoute = () => {
    navigate('/routes/optimization');
  };

  const handleViewRoute = (routeId: string) => {
    navigate(`/routes/${routeId}`);
  };

  const handleEditRoute = (routeId: string) => {
    navigate(`/routes/${routeId}/edit`);
  };

  const handleDeleteClick = (route: Route) => {
    setRouteToDelete(route);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!routeToDelete?._id) return;
    
    setDeleteLoading(true);
    try {
      const response = await routeService.deleteRoute(routeToDelete._id);
      
      if (!response.error) {
        setSnackbar({
          open: true,
          message: 'Rota excluída com sucesso',
          severity: 'success'
        });
        fetchRoutes();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Erro ao excluir rota',
          severity: 'error'
        });
      }
    } catch (err: any) {
      console.error('Erro ao excluir rota:', err);
      setSnackbar({
        open: true,
        message: `Erro ao excluir rota: ${err.message || 'Erro desconhecido'}`,
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setRouteToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRouteToDelete(null);
  };

  const handleStartRoute = async (routeId: string) => {
    try {
      const response = await routeService.startRoute(routeId, { startTime: new Date().toISOString() });
      
      if (response.data?.success) {
        setSnackbar({
          open: true,
          message: 'Rota iniciada com sucesso',
          severity: 'success'
        });
        fetchRoutes();
      } else {
        setSnackbar({
          open: true,
          // @ts-ignore - Propriedade error não existe no tipo AxiosResponse
          message: response.error || 'Erro ao iniciar rota',
          severity: 'error'
        });
      }
    } catch (err: any) {
      console.error('Erro ao iniciar rota:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao iniciar rota. Por favor, tente novamente.',
        severity: 'error'
      });
    }
  };

  const handleCompleteRoute = async (routeId: string) => {
    try {
      const response = await routeService.completeRoute(routeId, { endTime: new Date().toISOString() });
      
      if (response.data?.success) {
        setSnackbar({
          open: true,
          message: 'Rota concluída com sucesso',
          severity: 'success'
        });
        fetchRoutes();
      } else {
        setSnackbar({
          open: true,
          // @ts-ignore - Propriedade error não existe no tipo AxiosResponse
          message: response.error || 'Erro ao concluir rota',
          severity: 'error'
        });
      }
    } catch (err: any) {
      console.error('Erro ao concluir rota:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao concluir rota. Por favor, tente novamente.',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Função para obter o rótulo do status
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendente';
      case 'in_progress':
        return 'Em Andamento';
      case 'completed':
        return 'Concluída';
      case 'cancelled':
        return 'Cancelada';
      default:
        return 'Desconhecido';
    }
  };

  // Função para obter a cor do status
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Função para formatar a data
  const formatDate = (date?: string | Date) => {
    if (!date) return '-';
    try {
      // Se já for um objeto Date, usar diretamente
      const dateObj = date instanceof Date ? date : new Date(date);
      console.log('Estado atual - data:', date);
      console.log('Estado atual - dateObj:', dateObj);
      return format(dateObj, 'dd/MM/yyyy', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      console.log('Estado atual - rotas:', routes);
      console.log('Estado atual - total:', totalRoutes);
      console.log('Estado atual - loading:', loading);
      console.log('Estado atual - error:', error);
      return '-';
    }
  };

  return (
    <AuthLayout title="Rotas">
      <Container maxWidth="xl" sx={{ mt: 3, mb: 5 }}>
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5" component="h1" gutterBottom>
                Gerenciamento de Rotas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visualize, crie e gerencie rotas para os técnicos de campo.
              </Typography>
              <Typography variant="body2" color="primary">
                Total de rotas: {routes.length} | Carregando: {loading ? 'Sim' : 'Não'} | Erro: {error ? 'Sim' : 'Não'}
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddRoute}
              >
                Nova Rota
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Paper elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  placeholder="Buscar rotas..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    label="Status"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="pending">Pendente</MenuItem>
                    <MenuItem value="in_progress">Em Andamento</MenuItem>
                    <MenuItem value="completed">Concluída</MenuItem>
                    <MenuItem value="cancelled">Cancelada</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: showFilters ? 'block' : 'none' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker
                    label="Data Inicial"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: showFilters ? 'block' : 'none' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker
                    label="Data Final"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    size="small"
                  >
                    {showFilters ? 'Ocultar Filtros' : 'Mais Filtros'}
                  </Button>
                  {(searchTerm || statusFilter || startDate || endDate) && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleClearFilters}
                      size="small"
                    >
                      Limpar
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {loading && routes.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Técnico</TableCell>
                    <TableCell>Data de Execução</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Distância</TableCell>
                    <TableCell>Tempo Estimado</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {routes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body1" color="text.secondary">
                          Nenhuma rota encontrada
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    routes.map((route) => (
                      <TableRow hover key={route._id}>
                        <TableCell>{route.name}</TableCell>
                        <TableCell>
                          {typeof route.technician === 'object' && route.technician !== null
                            ? route.technician.name
                            : route.technician || '-'}
                        </TableCell>
                        <TableCell>{formatDate(route.executionDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(route.status || 'pending')}
                            color={getStatusColor(route.status || 'pending')}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {route.totalDistance
                            ? `${(route.totalDistance / 1000).toFixed(1)} km`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {route.estimatedTime
                            ? `${Math.round(route.estimatedTime / 60)} min`
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Tooltip title="Visualizar">
                              <IconButton
                                size="small"
                                onClick={() => handleViewRoute(route._id as string)}
                                sx={{ color: '#2196f3' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            {route.status === 'pending' && (
                              <>
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditRoute(route._id as string)}
                                    sx={{ color: '#ff9800' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Iniciar Rota">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleStartRoute(route._id as string)}
                                    sx={{ color: '#4caf50' }}
                                  >
                                    <StartIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            
                            {route.status === 'in_progress' && (
                              <Tooltip title="Concluir Rota">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCompleteRoute(route._id as string)}
                                  sx={{ color: '#4caf50' }}
                                >
                                  <CompleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {(route.status === 'pending' || route.status === 'in_progress') && (
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(route)}
                                  sx={{ color: '#f44336' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalRoutes}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>

        {/* Diálogo de confirmação de exclusão */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
        >
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir a rota "{routeToDelete?.name}"?
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              disabled={deleteLoading}
              startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
            >
              {deleteLoading ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para mensagens */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AuthLayout>
  );
};

export default RouteList;